import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PrivacyLinks from "@/components/PrivacyLinks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle, Loader2, Calendar, Clock, User, Mail, Phone, ArrowRight, XCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function fmt(val) {
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(val);
}

// Generate available dates (next 14 business days)
function getAvailableDates() {
  const dates = [];
  const now = new Date();
  let d = new Date(now);
  d.setDate(d.getDate() + 1); // start from tomorrow
  while (dates.length < 14) {
    const day = d.getDay();
    if (day !== 0 && day !== 6) { // no weekends
      dates.push(new Date(d));
    }
    d.setDate(d.getDate() + 1);
  }
  return dates;
}

const TIME_SLOTS = ["09:00", "09:30", "10:00", "10:30", "11:00", "11:30", "14:00", "14:30", "15:00", "15:30", "16:00", "16:30", "17:00"];

export default function ConsulenzaConfermaPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");

  const [status, setStatus] = useState("loading"); // loading | paid | error | booked
  const [paymentData, setPaymentData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");
  const [form, setForm] = useState({ nome: "", cognome: "", email: "", telefono: "" });
  const [submitting, setSubmitting] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const pollPayment = useCallback(async (attempts = 0) => {
    if (!sessionId || attempts >= 8) {
      setStatus("error");
      return;
    }
    try {
      const resp = await fetch(`${API}/api/pagamenti/status/${sessionId}`);
      if (!resp.ok) throw new Error("Errore");
      const data = await resp.json();
      if (data.payment_status === "paid") {
        setPaymentData(data);
        setStatus("paid");
        return;
      }
      if (data.status === "expired") {
        setStatus("error");
        return;
      }
      // keep polling
      setPollCount(attempts + 1);
      setTimeout(() => pollPayment(attempts + 1), 2000);
    } catch {
      setTimeout(() => pollPayment(attempts + 1), 2000);
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    pollPayment(0);
  }, [sessionId, pollPayment]);

  const availableDates = getAvailableDates();

  const handleSubmitBooking = async () => {
    if (!selectedDate || !selectedTime || !form.nome || !form.email) return;
    setSubmitting(true);
    try {
      const dateStr = selectedDate.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
      const resp = await fetch(`${API}/api/prenotazioni`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          transaction_id: paymentData.transaction_id,
          data: dateStr,
          ora: selectedTime,
          nome: form.nome,
          cognome: form.cognome,
          email: form.email,
          telefono: form.telefono,
        }),
      });
      if (!resp.ok) {
        const err = await resp.json();
        throw new Error(err.detail || "Errore");
      }
      const data = await resp.json();
      setBookingResult(data);
      setStatus("booked");
    } catch (err) {
      alert(err.message || "Errore nella prenotazione. Riprova.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="consulenza-conferma-page">
        <div className="max-w-2xl mx-auto px-5 sm:px-6 py-12">

          {/* LOADING */}
          {status === "loading" && (
            <div className="text-center py-20" data-testid="payment-loading">
              <Loader2 className="w-10 h-10 text-[#163321] animate-spin mx-auto mb-4" />
              <h2 className="font-heading text-xl font-medium text-[#0F1F14] mb-2">Verifica pagamento in corso...</h2>
              <p className="text-sm text-[#4A5E51]">Stiamo confermando il tuo pagamento. Non chiudere questa pagina.</p>
              {pollCount > 2 && <p className="text-xs text-[#4A5E51] mt-2">Elaborazione in corso...</p>}
            </div>
          )}

          {/* ERROR */}
          {status === "error" && (
            <div className="text-center py-20" data-testid="payment-error">
              <div className="w-16 h-16 mx-auto mb-4 bg-red-50 rounded-full flex items-center justify-center">
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="font-heading text-xl font-medium text-[#0F1F14] mb-2">Pagamento non riuscito</h2>
              <p className="text-sm text-[#4A5E51] mb-6">Il pagamento non e' andato a buon fine o la sessione e' scaduta.</p>
              <Button onClick={() => navigate("/consulenza")} className="rounded-xl bg-[#163321] text-white hover:bg-[#224A32]" data-testid="back-to-consulenza-btn">
                Torna alla pagina consulenza
              </Button>
            </div>
          )}

          {/* PAID - BOOKING FORM */}
          {status === "paid" && paymentData && (
            <div data-testid="booking-section">
              {/* Success banner */}
              <div className="bg-green-50 border border-green-200 rounded-2xl p-5 mb-8 flex items-center gap-4" data-testid="payment-success-banner">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center shrink-0">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h2 className="font-heading text-base font-medium text-green-800">Pagamento confermato!</h2>
                  <p className="text-sm text-green-700">{paymentData.package_nome} — {fmt(paymentData.amount)}</p>
                </div>
              </div>

              <h2 className="font-heading text-xl font-medium text-[#0F1F14] mb-2">Scegli data e ora</h2>
              <p className="text-sm text-[#4A5E51] mb-6">Seleziona il giorno e l'orario che preferisci per la tua consulenza.</p>

              {/* Date picker */}
              <div className="mb-6">
                <Label className="text-sm font-medium text-[#0F1F14] mb-3 block">Giorno</Label>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="date-picker">
                  {availableDates.map((date, i) => {
                    const isSelected = selectedDate?.toDateString() === date.toDateString();
                    const dayName = date.toLocaleDateString("it-IT", { weekday: "short" });
                    const dayNum = date.getDate();
                    const month = date.toLocaleDateString("it-IT", { month: "short" });
                    return (
                      <button
                        key={i}
                        onClick={() => setSelectedDate(date)}
                        className={`p-3 rounded-xl border-2 text-center transition-all ${isSelected ? "border-[#163321] bg-[#163321]/10" : "border-[#E5E2DB] hover:border-[#163321]/30"}`}
                        data-testid={`date-btn-${i}`}
                      >
                        <p className="text-[10px] uppercase text-[#4A5E51]">{dayName}</p>
                        <p className={`text-lg font-heading font-medium ${isSelected ? "text-[#163321]" : "text-[#0F1F14]"}`}>{dayNum}</p>
                        <p className="text-[10px] text-[#4A5E51]">{month}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Time slots */}
              {selectedDate && (
                <div className="mb-6">
                  <Label className="text-sm font-medium text-[#0F1F14] mb-3 block">Orario</Label>
                  <div className="grid grid-cols-4 sm:grid-cols-7 gap-2" data-testid="time-picker">
                    {TIME_SLOTS.map(t => (
                      <button
                        key={t}
                        onClick={() => setSelectedTime(t)}
                        className={`p-2.5 rounded-xl border-2 text-center transition-all text-sm ${selectedTime === t ? "border-[#163321] bg-[#163321]/10 font-medium text-[#163321]" : "border-[#E5E2DB] hover:border-[#163321]/30 text-[#4A5E51]"}`}
                        data-testid={`time-btn-${t.replace(":", "")}`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Contact form */}
              {selectedDate && selectedTime && (
                <div className="bg-white rounded-2xl border border-[#E5E2DB] p-6 mb-6 space-y-4" data-testid="booking-contact-form">
                  <h3 className="font-heading text-base font-medium text-[#0F1F14]">I tuoi dati</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-[#4A5E51]">Nome *</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5E51]" />
                        <Input value={form.nome} onChange={e => setForm(p => ({ ...p, nome: e.target.value }))} className="pl-10 rounded-xl border-[#E5E2DB] h-11" placeholder="Mario" data-testid="booking-nome" />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-[#4A5E51]">Cognome</Label>
                      <Input value={form.cognome} onChange={e => setForm(p => ({ ...p, cognome: e.target.value }))} className="rounded-xl border-[#E5E2DB] h-11" placeholder="Rossi" data-testid="booking-cognome" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-[#4A5E51]">Email *</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5E51]" />
                        <Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="pl-10 rounded-xl border-[#E5E2DB] h-11" placeholder="mario@email.com" data-testid="booking-email" />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-[#4A5E51]">Telefono</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5E51]" />
                        <Input value={form.telefono} onChange={e => setForm(p => ({ ...p, telefono: e.target.value }))} className="pl-10 rounded-xl border-[#E5E2DB] h-11" placeholder="+39 333 1234567" data-testid="booking-telefono" />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Summary + Submit */}
              {selectedDate && selectedTime && form.nome && form.email && (
                <div className="bg-gradient-to-br from-[#163321] to-[#224A32] rounded-2xl p-6 text-white" data-testid="booking-summary">
                  <div className="flex items-center gap-3 mb-4">
                    <Calendar className="w-5 h-5 text-[#D97B58]" />
                    <div>
                      <p className="text-sm font-medium">
                        {selectedDate.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                      </p>
                      <p className="text-xs text-white/60">Ore {selectedTime}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mb-6">
                    <User className="w-5 h-5 text-[#D97B58]" />
                    <p className="text-sm">{form.nome} {form.cognome} — {form.email}</p>
                  </div>
                  <Button
                    onClick={handleSubmitBooking}
                    disabled={submitting}
                    className="w-full rounded-xl h-12 bg-[#D97B58] text-white hover:bg-[#C06A48] text-sm font-medium"
                    data-testid="confirm-booking-btn"
                  >
                    {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Conferma in corso...</> : <>Conferma Prenotazione <ArrowRight className="w-4 h-4 ml-2" /></>}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* BOOKED */}
          {status === "booked" && bookingResult && (
            <div className="text-center py-16" data-testid="booking-confirmed">
              <div className="w-20 h-20 mx-auto mb-6 bg-green-50 rounded-full flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="font-heading text-2xl font-medium text-[#0F1F14] mb-2">Prenotazione Confermata!</h2>
              <p className="text-sm text-[#4A5E51] mb-6 max-w-md mx-auto">
                La tua consulenza e' stata prenotata per il <strong>{bookingResult.data}</strong> alle ore <strong>{bookingResult.ora}</strong>.
                Riceverai una conferma via email con tutti i dettagli.
              </p>
              <div className="bg-[#F5F4F0] rounded-2xl p-6 max-w-sm mx-auto mb-8">
                <div className="flex items-center gap-3 justify-center mb-2">
                  <Calendar className="w-5 h-5 text-[#163321]" />
                  <p className="font-heading text-base font-medium text-[#0F1F14]">{bookingResult.data}</p>
                </div>
                <div className="flex items-center gap-3 justify-center">
                  <Clock className="w-5 h-5 text-[#163321]" />
                  <p className="text-sm text-[#4A5E51]">Ore {bookingResult.ora}</p>
                </div>
              </div>
              <Button onClick={() => navigate("/")} className="rounded-xl bg-[#163321] text-white hover:bg-[#224A32]" data-testid="back-home-btn">
                Torna alla Home
              </Button>
            </div>
          )}
        <PrivacyLinks className="mt-6 mb-4 max-w-2xl mx-auto px-5" />
        </div>
      </main>
      <Footer />
    </>
  );
}
